"use client";

import { useEffect, useMemo, useState } from "react";

import { SYSTEM_ATTRIBUTE_PROPERTY } from "~/core/constants/attributes";
import { OBJECT_TYPE } from "~/core/constants/common";
import { useGlobalObjectSearch } from "~/core/hooks/objects/useGlobalSearch";

import { AccountLabel } from "../components/FormItems/AccountLabel";
import { useGlobalNewOpportunityModalContext } from "../context";
import { useGlobalCreateOpportunityStore } from "../store";
import { DDRendererItem } from "../types";

export const useContactMultiSelect = () => {
  const [openSelect, setOpenSelect] = useState(false);
  const { form } = useGlobalNewOpportunityModalContext();
  const {
    setMultiSelectContacts,
    multiSelectContacts,
    setSelectedMultiSelectContact,
    selectedAccount,
  } = useGlobalCreateOpportunityStore();
  const [searchQuery, setSearchQuery] = useState("");

  // Helper function to ensure value is always an array
  const ensureArray = (value: any) => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  };

  useEffect(() => {
    if (form) {
      const currentValue = form.getFieldValue(
        SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_OTHER_STAKEHOLDERS,
      );
      const normalizedValue = ensureArray(currentValue);

      if (currentValue !== normalizedValue) {
        form.setFieldValue(
          SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_OTHER_STAKEHOLDERS,
          normalizedValue,
        );
      }
    }
  }, [form]);

  const { objectSearchData } = useGlobalObjectSearch({
    searchQuery: searchQuery,
    objectType: OBJECT_TYPE.CONTACT,
  });

  /**
   * When the account changes, set the search query to the account domain
   */
  useEffect(() => {
    if (!selectedAccount) return;
    setSearchQuery(selectedAccount?.metadata?.description);
  }, [selectedAccount]);

  const options = useMemo(() => {
    return objectSearchData?.map(item => ({
      id: item.object_id,
      label: <AccountLabel item={item} />,
      active: false,
      searchableText: item.title || item.description,
      metadata: {
        description: item.description,
        title: item.title || item.description,
        image_url: item.image_url,
      },
    }));
  }, [objectSearchData]);

  useEffect(() => {
    // Get currently selected contacts
    const selectedContacts =
      multiSelectContacts?.filter(option => option.active) || [];

    // Filter out options that are already in selected contacts (comparing by id)
    const uniqueNewOptions =
      options?.filter(
        option => !selectedContacts.some(selected => selected.id === option.id),
      ) || [];

    // Update form value with selected contact ids
    form?.setFieldValue(
      SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_OTHER_STAKEHOLDERS,
      selectedContacts.map(item => item.id),
    );

    // Combine selected contacts with unique new options
    setMultiSelectContacts([...selectedContacts, ...uniqueNewOptions]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectSearchData]);

  const handleSelect = (e: DDRendererItem) => {
    const currentValue = form?.getFieldValue(
      SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_OTHER_STAKEHOLDERS,
    );
    const normalizedValue = ensureArray(currentValue);

    // Check if value exists in array
    const valueExists = normalizedValue.includes(e.id);

    // Remove if exists, add if doesn't exist
    const newValue = valueExists
      ? normalizedValue.filter(val => val !== e.id)
      : [...normalizedValue, e.id];

    form?.setFieldValue(
      SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_OTHER_STAKEHOLDERS,
      newValue,
    );

    setSelectedMultiSelectContact(e);
  };

  useEffect(() => {
    const isLastOption =
      multiSelectContacts.filter(item => item.active).length === 0 &&
      searchQuery.length === 0;
    if (isLastOption) {
      setOpenSelect(false);
    }
  }, [multiSelectContacts, searchQuery]);

  return {
    openSelect,
    setOpenSelect,
    ensureArray,
    handleSelect,
    searchQuery,
    setSearchQuery,
  };
};
